<script setup lang="ts" generic="T,K">
import { useVModel } from '@vueuse/core';
import { useSlots, VNodeProps } from 'vue';

import RadioGroupItem from '@/components/base/form/RadioGroupItem.vue';

const props = defineProps<{
  appearanceNone?: boolean;
  dataTest?: string | ((value: T) => string);
  disabled?: boolean | ((value: T) => boolean);
  /**
   * If `true`, the border style position of the RadioGroupItem will not be automatically set.
   * @default false
   */
  ignorePosition?: boolean;
  items: T[];
  keyGetter: (value: T) => VNodeProps['key'];
  modelValue: K;
  name: string;
  valueGetter: (value: T) => K;
}>();

const emit = defineEmits<{ 'update:modelValue': [K] }>();
const slots = useSlots();

const selected = useVModel(props, 'modelValue', emit);

const determinePosition = (index: number) => {
  if (props.items.length === 1) {
    if (slots['top-options'] && slots['bottom-options']) return 'middle';
    if (slots['top-options']) return 'last';
    if (slots['bottom-options']) return 'first';
    return undefined;
  }
  if (index === 0) return slots['top-options'] ? 'middle' : 'first';
  if (index === props.items.length - 1) return slots['bottom-options'] ? 'middle' : 'last';
  return 'middle';
};
</script>

<template>
  <div>
    <slot name="top-options" />
    <RadioGroupItem
      v-for="(item, index) in items"
      v-model="selected"
      :appearanceNone
      :disabled="typeof disabled === 'function' ? disabled(item) : disabled"
      :data-test="typeof dataTest === 'function' ? dataTest(item) : dataTest"
      :key="keyGetter(item)"
      :id="`${name}-${index}`"
      :name="name"
      :position="ignorePosition ? undefined : determinePosition(index)"
      :value="valueGetter(item)"
    >
      <template v-for="(_, slot) of $slots" #[slot]>
        <slot :name="slot" :item="item" />
      </template>
    </RadioGroupItem>
    <slot name="bottom-options" />
  </div>
</template>
